import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Target, Heart, Award, ArrowRight, Users, Clock } from 'lucide-react';
import '../styles/AboutPage.css';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Leaf size={36} />,
      title: "100% Naturel",
      description: "Nous utilisons exclusivement des produits naturels et naturels pour protéger votre famille et l'environnement."
    },
    {
      icon: <Award size={36} />,
      title: "Excellence",
      description: "Notre équipe certifiée et expérimentée offre un service professionnel de la plus haute qualité."
    },
    {
      icon: <Heart size={36} />,
      title: "Passion",
      description: "Nous aimons ce que nous faisons et cela se reflète dans chaque projet que nous réalisons."
    },
    {
      icon: <Users size={36} />,
      title: "Service personnalisé",
      description: "Chaque pelouse est unique. Nous adaptons nos services à vos besoins spécifiques."
    }
  ];

  const stats = [
    { number: "500+", label: "Clients satisfaits" },
    { number: "100%", label: "Produits naturels" },
    { number: "4.9/5", label: "Note moyenne" },
    { number: "Bromont", label: "Région desservie" }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <p className="contact-subtitle">QUI SOMMES-NOUS</p>
          <h1 className="display-large text-center">À propos de Naturellement Vert</h1>
          <div className="title-underline"></div>
          <p className="body-large text-center" style={{maxWidth: '700px', margin: '0 auto'}}>
            Votre partenaire de confiance pour l'entretien naturel de votre espace vert en Bromont.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-text">
              <div className="mission-icon">
                <Target size={50} />
              </div>
              <h2 className="heading-1 mb-md">Notre mission</h2>
              <p className="body-large mb-md">
                Fondée par <strong>Brandon Théorêt</strong>, NVF – Naturellement Vert Fertilisation est une entreprise spécialisée dans l'entretien paysager en Bromont. Notre passion pour les espaces verts et notre souci du détail nous permettent d'offrir des services de qualité supérieure à nos clients.
              </p>
              <p className="body-large mb-md">
                Que ce soit pour la taille de vos haies, la fertilisation de votre pelouse ou le contrôle des mauvaises herbes, nous utilisons des méthodes éprouvées et des produits de qualité pour assurer la santé et la beauté de votre terrain.
              </p>
              <p className="body-large">
                Depuis nos débuts, nous servons fièrement les résidents et entreprises de l'Bromont avec des solutions naturels qui produisent des résultats durables et visibles. Notre engagement envers l'excellence et la satisfaction client nous distingue dans l'industrie.
              </p>
            </div>

            <div className="mission-image">
              <div className="image-placeholder">
                <Leaf size={80} />
                <p>Image: Équipe NVF au travail</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section">
        <div className="container">
          <h2 className="display-medium text-center mb-xl">Nos valeurs</h2>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card nvf-card hover-lift">
                <div className="value-icon">{value.icon}</div>
                <h3 className="heading-3 mb-sm">{value.title}</h3>
                <p className="body-medium">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section section bg-subtle">
        <div className="container">
          <div className="why-choose-content">
            <div className="why-choose-image">
              <div className="image-placeholder">
                <Users size={80} />
                <p>Image: Expertise NVF</p>
              </div>
            </div>

            <div className="why-choose-text">
              <h2 className="heading-1 mb-md">Pourquoi nous choisir?</h2>
              
              <div className="why-points">
                <div className="why-point">
                  <div className="why-point-icon">
                    <Award size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-xs">Expertise certifiée</h3>
                    <p className="body-medium">
                      Notre équipe possède les certifications et l'expérience nécessaires pour garantir un travail professionnel et sécuritaire.
                    </p>
                  </div>
                </div>

                <div className="why-point">
                  <div className="why-point-icon">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-xs">Service rapide</h3>
                    <p className="body-medium">
                      Nous respectons vos horaires et offrons un service ponctuel et efficace pour minimiser les désagréments.
                    </p>
                  </div>
                </div>

                <div className="why-point">
                  <div className="why-point-icon">
                    <Leaf size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-xs">Engagement naturel</h3>
                    <p className="body-medium">
                      Nous sommes dédiés à la protection de l'environnement et utilisons uniquement des produits naturels certifiés.
                    </p>
                  </div>
                </div>

                <div className="why-point">
                  <div className="why-point-icon">
                    <Heart size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-xs">Satisfaction garantie</h3>
                    <p className="body-medium">
                      Votre satisfaction est notre priorité. Nous nous engageons à dépasser vos attentes à chaque intervention.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta section">
        <div className="container">
          <div className="cta-box">
            <h2 className="display-medium text-center mb-md">
              Prêt à travailler avec nous?
            </h2>
            <p className="body-large text-center mb-lg" style={{maxWidth: '600px', margin: '0 auto 32px', color: 'white'}}>
              Rejoignez les centaines de clients satisfaits qui nous font confiance pour l'entretien de leur espace vert.
            </p>
            <div className="text-center">
              <button className="btn-primary btn-large" onClick={() => navigate('/contact')}>
                Demander une soumission gratuite
                <ArrowRight size={22} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
